import { ApiRoutes, AppPaginationOptions, QueryError, WithPagination } from '@/common/interfaces';
import { apiExecutor, buildMutation, buildQuery } from '@/common/services';

import {
  BulkDeleteExternalRedirectsPayload,
  BulkUpdateExternalRedirectsActivityPayload,
  ExternalRedirect,
  ExternalRedirectConflicts,
  ExternalRedirectFormFields,
  ExternalRedirectsQueryKeys,
} from '@/modules/ExternalRedirects';

export const useGetAllExternalRedirectsQuery = (siteId: string, paginationOptions: AppPaginationOptions) =>
  buildQuery<WithPagination<ExternalRedirect>, QueryError>(
    [ExternalRedirectsQueryKeys.GetAll, siteId, { ...paginationOptions }],
    async () => {
      const { data } = await apiExecutor.get<WithPagination<ExternalRedirect>>(
        ApiRoutes.ExternalRedirects.replace(':siteId', siteId),
        { params: { ...paginationOptions } }
      );
      return data;
    }
  );

export const useGetOneByIdExternalRedirectsQuery = (siteId: string, externalRedirectId: string) =>
  buildQuery<ExternalRedirect, QueryError>(
    [ExternalRedirectsQueryKeys.GetOneById, siteId, externalRedirectId],
    async () => {
      const { data } = await apiExecutor.get<ExternalRedirect>(
        ApiRoutes.ExternalRedirectsById.replace(':siteId', siteId).replace(':externalRedirectId', externalRedirectId)
      );
      return data;
    }
  );

export const useGetExternalRedirectsConflictsQuery = (siteId: string) =>
  buildQuery<ExternalRedirectConflicts, QueryError>([ExternalRedirectsQueryKeys.Conflicts, siteId], async () => {
    const { data } = await apiExecutor.get<ExternalRedirectConflicts>(
      ApiRoutes.ExternalRedirectsConflicts.replace(':siteId', siteId)
    );
    return data;
  });

export const useCreateExternalRedirectsMutation = (siteId: string) =>
  buildMutation<ExternalRedirect, QueryError, ExternalRedirectFormFields>(
    [ExternalRedirectsQueryKeys.Create, siteId],
    async (postData) => {
      const { data } = await apiExecutor.post<ExternalRedirect>(
        ApiRoutes.ExternalRedirects.replace(':siteId', siteId),
        { ...postData }
      );
      return data;
    }
  );

export const useEditExternalRedirectsMutation = (siteId: string, externalRedirectId: string) =>
  buildMutation<ExternalRedirect, QueryError, ExternalRedirectFormFields>(
    [ExternalRedirectsQueryKeys.Edit, siteId, externalRedirectId],
    async (postData) => {
      const { data } = await apiExecutor.patch<ExternalRedirect>(
        ApiRoutes.ExternalRedirectsById.replace(':siteId', siteId).replace(':externalRedirectId', externalRedirectId),
        { ...postData, id: externalRedirectId }
      );
      return data;
    }
  );

export const useDeleteExternalRedirectsMutation = (siteId: string, externalRedirectId: string) =>
  buildMutation<void, QueryError, void>([ExternalRedirectsQueryKeys.Delete, siteId, externalRedirectId], async () => {
    await apiExecutor.delete<void>(
      ApiRoutes.ExternalRedirectsById.replace(':siteId', siteId).replace(':externalRedirectId', externalRedirectId)
    );
  });

export const useBulkUpdateActivityExternalRedirectsMutation = (siteId: string) =>
  buildMutation<void, QueryError, BulkUpdateExternalRedirectsActivityPayload>(
    [ExternalRedirectsQueryKeys.BulkActivity, siteId],
    async (postData) => {
      await apiExecutor.patch<void>(ApiRoutes.ExternalRedirectsBulkActivity.replace(':siteId', siteId), { ...postData });
    }
  );

export const useBulkDeleteExternalRedirectsMutation = (siteId: string) =>
  buildMutation<void, QueryError, BulkDeleteExternalRedirectsPayload>(
    [ExternalRedirectsQueryKeys.BulkDelete, siteId],
    async (postData) => {
      await apiExecutor.post<void>(ApiRoutes.ExternalRedirectsBulkDelete.replace(':siteId', siteId), { ...postData });
    }
  );

export const useBreadcrumbExternalRedirectsQuery = (siteId: string, externalRedirectId: string) =>
  buildQuery<string, QueryError>([ExternalRedirectsQueryKeys.Breadcrumb, siteId, externalRedirectId], async () => {
    const { data } = await apiExecutor.get<string>(
      ApiRoutes.ExternalRedirectsBreadcrumb.replace(':siteId', siteId).replace(':externalRedirectId', externalRedirectId)
    );
    return data;
  });

